import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { Location, useLocation } from "react-router-dom"

import queryClient from "@/api/client"
import StreamAPISocket from "@/api/stable/APISocket"
import DefaultBoundaries from "@/app/boundaries/DefaultBoundaries"
import AppEditLayout from "@/app/layouts/AppEditLayout/AppEditLayout"
import PageLayout from "@/app/layouts/PageLayout/PageLayout"
import { MenuAppInstanceView } from "@/areas/app"
import { LocationContainer, LocationGrouped, LocationGroupedComponentProps } from "@/areas/location"
// eslint-disable-next-line no-restricted-imports
import LocationHeader from "@/areas/location/header/LocationHeader"
import { MenuAppSettingsContainer, MenuAppsGroup, PopupCreateMenuApp } from "@/areas/menu"
import { AppInstance } from "@/entities/app/app.types"
import MenuAppDAO from "@/entities/app/menu/menu.dao"
import OnlineMenusSource from "@/entities/app/menu/menu.online"
import { confirmAction } from "@/services/Modal/components/PopupConfirm"
import Modal from "@/services/Modal/Modal"
import Theme from "@/services/Theme/Theme"
import ButtonIcon from "@/shared/components/intrinsic/Button/ButtonIcon"
import Icon, { IconName } from "@/shared/components/intrinsic/Icon/Icon"
import { useAppSelector } from "@/store/hooks"
import useParam from "@/utils/hooks/useParam"
import useResource from "@/utils/hooks/useResource"
import useSearchState from "@/utils/hooks/useSearchState"

interface LocationState {
  dirty?: boolean
}

function MenusPage() {
  const companyId = useParam("id")
  const [appId] = useSearchState("appId")
  const locationTab = useAppSelector(state => state.location)
  const location = useLocation() as Location<LocationState>
  location.state ??= {}

  const sidebar = (
    <DefaultBoundaries>
      {locationTab === "Locations" && !!appId && <MenuAppSettingsContainer id={appId} onChange={() => location.state.dirty = true} />}
    </DefaultBoundaries>
  )
  return (
    <AppEditLayout sidebar={sidebar} sidebarActive={true}>
      <PageLayout>
        <Theme scheme="dark">
          <DefaultBoundaries>
            <LocationHeader companyId={companyId} title="Menu Settings" />
            <LocationGrouped companyId={companyId} children={MenuAppsByLocation} />
          </DefaultBoundaries>
        </Theme>
      </PageLayout>
    </AppEditLayout>
  )
}

export default MenusPage


const menuAppDao = new MenuAppDAO

function MenuAppsByLocation(props: LocationGroupedComponentProps) {
  const [appId] = useSearchState("appId")

  const { data: menu } = useQuery({
    queryFn: () => appId != null ? menuAppDao.findById(appId) : null,
    queryKey: [menuAppDao.name, appId]
  })

  const { data: menuApps } = useQuery({
    queryFn: () => menuAppDao.findByLocation(props.location.id),
    queryKey: [menuAppDao.name, "location", props.location.id]
  })

  const calendarIcon: IconName = useMemo(() => {
    if (!menuApps || menuApps.length === 0) return "calendar_pending"
    const hasAnyInstanceWithoutEvents = menuApps.some(app => !app.events || app.events.length === 0)
    return hasAnyInstanceWithoutEvents ? "calendar_pending" : "calendar_success"
  }, [menuApps])

  return (
    <LocationContainer {...props} pageContext="menus" calendarIcon={calendarIcon} defaultExpanded={props.location.id === menu?.locationId}>
      <DefaultBoundaries>
        <MenuAppsContainer {...props} />
      </DefaultBoundaries>
    </LocationContainer>
  )
}

function MenuAppsContainer(props: LocationGroupedComponentProps) {
  const [menuAppId, setMenuAppId] = useSearchState("appId")
  const location = useLocation() as Location<LocationState>

  const source = new StreamAPISocket("AdminPanel")

  const queryKey = [menuAppDao.name, "location", props.location.id, "groupedByScreen"]
  const { data: menuApps } = useSuspenseQuery({
    queryFn: () => menuAppDao.findByLocation(props.location.id),
    queryKey
  })
  const menuAppsGroupedByAppId = useMemo(() => Map.groupBy(menuApps, app => app.name), [menuApps])
  const activeAppIds = useResource(() => new OnlineMenusSource(props.companyId), [props.companyId], [])

  async function onMenuAppSelect(menuAppId: string) {
    if (location.state?.dirty) {
      await confirmAction()
    }

    setMenuAppId(menuAppId)
  }

  async function createMenuApp(appId: string, label: string) {
    const menuApp = await menuAppDao.create({
      name: appId,
      label,
      companyId: props.companyId,
      locationId: props.location.id
    })

    const queryKey = [menuAppDao.name, "location", props.location.id]
    queryClient.setQueriesData<AppInstance[]>({ queryKey, exact: false }, apps => apps && [...apps, menuApp])
  }

  function onMenuAppScreenCreate() {
    Modal.open(PopupCreateMenuApp, { ...props, onCreate: setMenuAppId })
  }

  return (
    <>
      <div className="app-instance__actions">
        <div className="app-instance__toggle">
          {activeAppIds.find(app => app.locationId === props.location.id) && <button
            type="button"
            title="Refresh"
            onClick={() => { source.socket.emit("REFRESH_MENUAPPS", { companyId: props.companyId, locationId: props.location.id }) }}
            key={props.location.id}
          >
            <Icon name="refresh" />
          </button>}
          <ButtonIcon size="small" name="plus" ariaLabel="Create Screen" onClick={onMenuAppScreenCreate} />
        </div>
      </div>
      {menuAppsGroupedByAppId.size === 0 && (
        <span>There are no Menus in this location.</span>
      )}
      {[...menuAppsGroupedByAppId.keys()].map(appId => (
        <MenuAppInstanceView type="MENU" name={appId} label={menuAppsGroupedByAppId.get(appId)?.[0]?.label || appId} active={(menuAppsGroupedByAppId.get(appId)?.length ?? 0) <= 1} key={appId} onCreate={() => createMenuApp(appId, menuAppsGroupedByAppId.get(appId)?.[0]?.label || appId)} instances={menuAppsGroupedByAppId.get(appId)} refreshInstance={() => { source.socket.emit("REFRESH_MENUAPP", { appId }) }}>
          <MenuAppsGroup appId={appId} instanceId={menuAppId} activeList={activeAppIds.filter(app => app.appId === appId)} instances={menuAppsGroupedByAppId.get(appId)!} onSelect={onMenuAppSelect} />
        </MenuAppInstanceView>
      ))}
    </>
  )
}
